import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import {
  FiArrowLeft, FiUser, FiMail, FiGitBranch,
  FiBell, FiAlertTriangle, FiClock, FiZap, FiCopy,
} from "react-icons/fi";
import toast from "react-hot-toast";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-sm font-medium text-gray-800 break-all ${mono ? "font-mono text-xs" : ""}`}>
      {value ?? "—"}
    </p>
  </div>
);

const Section = ({ title, icon: Icon, iconColor = "text-blue-500", iconBg = "bg-blue-50", children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
        <Icon size={14} className={iconColor} />
      </div>
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="px-6 py-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">{children}</div>
  </div>
);

const Badge = ({ value, trueLabel = "Yes", falseLabel = "No", trueColor = "green" }) => {
  const on = Boolean(value);
  const colorMap = {
    green:  "bg-green-100 text-green-700",
    gray:   "bg-gray-100  text-gray-500",
    orange: "bg-orange-100 text-orange-600",
    blue:   "bg-blue-100  text-blue-600",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[on ? trueColor : "gray"]}`}>
      {on ? trueLabel : falseLabel}
    </span>
  );
};

const StatCard = ({ label, value, sub, color = "blue" }) => {
  const colors = {
    blue:   "bg-blue-50   border-blue-100  text-blue-700",
    green:  "bg-green-50  border-green-100 text-green-700",
    orange: "bg-orange-50 border-orange-100 text-orange-600",
    purple: "bg-purple-50 border-purple-100 text-purple-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value ?? "—"}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
};

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${baseUrl}/users/view/${id}`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const result = await response.json();
        if (result?.status === true && result?.data) {
          setUser(result.data);
        } else {
          throw new Error(result?.message || "Failed to fetch user details");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const avatarLetter = (user?.username || "U")[0].toUpperCase();

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 max-w-5xl w-full space-y-5">

          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-600 transition"
          >
            <FiArrowLeft size={15} /> Back to Users
          </button>

          {loading ? (
            <div className="flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">{error}</div>
          ) : user ? (
            <>
              {/* Profile header */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  {/* Avatar */}
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                    {avatarLetter}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-800">{user.username || "—"}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
                    <p className="text-xs text-gray-400 font-mono mt-1">{user.uniqueCode}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge
                        value={user.isMiningActive}
                        trueLabel="Mining Active"
                        falseLabel="Mining Inactive"
                        trueColor="green"
                      />
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.providerType === "google" ? "bg-red-100 text-red-600"
                        : user.providerType === "apple" ? "bg-gray-100 text-gray-600"
                        : "bg-blue-100 text-blue-600"
                      }`}>
                        {user.providerType || "—"}
                      </span>
                      {user.strike > 0 && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600">
                          {user.strike} Strike{user.strike > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* User ID chip */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-gray-400">User ID</p>
                    <p className="text-2xl font-bold text-gray-700">#{user.userId}</p>
                    <p className="text-xs text-gray-400 mt-1">Joined {formatDate(user.createdAt).split(",")[0]}</p>
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  label="Total Coins"
                  value={parseFloat(user.coin || 0).toFixed(2)}
                  color="blue"
                />
                <StatCard
                  label="Mining Speed"
                  value={user.perHourMiningSpeed || "0"}
                  sub="per hour"
                  color="green"
                />
                <StatCard
                  label="Strike Count"
                  value={user.strike ?? 0}
                  color="orange"
                />
                <StatCard
                  label="Delete Requests"
                  value={user.deleteAccountCount ?? 0}
                  color="purple"
                />
              </div>

              {/* Account Information */}
              <Section title="Account Information" icon={FiUser} iconBg="bg-blue-50" iconColor="text-blue-500">
                <Field label="User ID" value={user.userId} />
                <Field label="Unique Code" value={user.uniqueCode} mono />
                <Field label="UUID" value={user.uuid} mono />
                <Field label="Provider Type" value={user.providerType} />
                <Field label="Social ID" value={user.socialId} mono />
                <Field label="Google ID" value={user.googleId} />
                <Field label="Apple ID" value={user.appleId} />
                <Field label="Delete Account Count" value={user.deleteAccountCount} />
              </Section>

              {/* Contact */}
              <Section title="Contact" icon={FiMail} iconBg="bg-indigo-50" iconColor="text-indigo-500">
                <Field label="Email" value={user.email} />
                <Field label="Username" value={user.username} />
              </Section>

              {/* Mining */}
              <Section title="Mining" icon={FiZap} iconBg="bg-yellow-50" iconColor="text-yellow-500">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Status</p>
                  <Badge value={user.isMiningActive} trueLabel="Active" falseLabel="Inactive" trueColor="green" />
                </div>
                <Field label="Total Coins" value={parseFloat(user.coin || 0).toFixed(5)} />
                <Field label="Per Hour Speed" value={user.perHourMiningSpeed} />
                <Field label="Last Coin Update" value={formatDate(user.lastUpdatedCoinTime)} />
              </Section>

              {/* Referral */}
              <Section title="Referral" icon={FiGitBranch} iconBg="bg-green-50" iconColor="text-green-500">
                <Field label="Referral Code" value={user.referralCode} mono />
                <Field label="Referred By" value={user.referralBy} mono />
                {user.referralLink ? (
                  <Field
                    label="Shareable Link"
                    value={
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-blue-700">{user.referralLink}</span>
                        <button
                          type="button"
                          title="Copy link"
                          onClick={() => {
                            navigator.clipboard.writeText(user.referralLink).then(
                              () => toast.success("Referral link copied"),
                              () => toast.error("Could not copy the link")
                            );
                          }}
                          className="text-gray-400 hover:text-blue-500 transition"
                        >
                          <FiCopy size={13} />
                        </button>
                      </div>
                    }
                  />
                ) : (
                  <Field
                    label="Shareable Link"
                    value={
                      <span className="text-xs text-gray-400">
                        Not available — the deep-link domain is not configured on the backend.
                      </span>
                    }
                  />
                )}
              </Section>

              {/* Notifications */}
              <Section title="Notification Preferences" icon={FiBell} iconBg="bg-purple-50" iconColor="text-purple-500">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Announcement</p>
                  <Badge value={user.isAnnouncementNotification} trueColor="green" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Mining</p>
                  <Badge value={user.isMiningNotification} trueColor="green" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Chat</p>
                  <Badge value={user.isChatNotification} trueColor="green" />
                </div>
              </Section>

              {/* Strike */}
              <Section title="Strike" icon={FiAlertTriangle} iconBg="bg-orange-50" iconColor="text-orange-500">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Count</p>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.strike > 0 ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                  }`}>
                    {user.strike ?? 0}
                  </span>
                </div>
                <Field label="Strike Rate" value={user.strikeRate} />
                <Field label="Updated At" value={formatDate(user.strikeUpdateDate)} />
              </Section>

              {/* Timestamps */}
              <Section title="Timestamps" icon={FiClock} iconBg="bg-gray-50" iconColor="text-gray-500">
                <Field label="Created At" value={formatDate(user.createdAt)} />
                <Field label="Updated At" value={formatDate(user.updatedAt)} />
              </Section>
            </>
          ) : (
            <p className="text-gray-500">No user data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
